<template>
  <div class="py-3">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
        <p-search-input
          v-model="search"
          :items="searchSuggestions"
        ></p-search-input>
      </div>
      <div v-if="hasAccess()" class="p-toolbar-item p-right">
        <v-btn
          class="p-btn-rounded mr-4"
          large
          rounded
          @click.stop="openEditDialog(null)"
        >
          {{ $t('budgetCategory.createCategory') }}
        </v-btn>
      </div>
    </div>

    <v-row>
      <v-col>
        <v-data-table
          :headers="tableHeaders"
          :search="search"
          :items="budgetCategoriesComputed"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          hide-default-footer
          class="p-no-borders"
          @page-count="pageCount = $event"
        >
          <template v-slot:[`item.changed`]="{ item }">
            <v-tooltip v-if="item.changed" bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on">
                  <span v-if="item.changed" class="item-changed"></span>
                </v-btn>
              </template>
              {{ $t('budgetCategory.table.changedTooltip') }}
            </v-tooltip>
          </template>
          <template v-slot:[`item.income`]="{ item }">
            <v-icon v-if="item.income" color="pGreen">mdi-plus</v-icon>
            <v-icon v-else color="pRed">mdi-minus</v-icon>
          </template>
          <template v-if="hasAccess()" v-slot:[`item.actions`]="{ item }">
            <div class="p-table-row-actions">
              <p-btn-icon type="edit" @click="openEditDialog(item)"></p-btn-icon>
              <p-btn-icon type="delete" @click="openDeleteDialog(item)"></p-btn-icon>
            </div>
          </template>
        </v-data-table>

        <v-pagination
          v-if="budgetCategories.length > 0"
          v-model="page"
          :length="pageCount"
          :total-visible="15"
          class="mt-5"
        ></v-pagination>
      </v-col>
    </v-row>

    <budget-category-edit-dialog
      :dialog.sync="editDialog"
      :budgetCategory="editBudgetCategory"
    ></budget-category-edit-dialog>

    <p-delete-dialog
      :dialog.sync="deleteDialog"
      :item="deletedBudgetCategory"
      :item-data="deletedBudgetCategoryData"
      :load-relations="loadRelations"
      :delete-item="deleteBudgetCategory"
      @itemDeleted="onItemDeleted"
    ></p-delete-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import PSearchInput from '@/components/PSearchInput'
import PBtnIcon from '@/components/PBtnIcon'
import PDeleteDialog from '@/components/PDeleteDialog'

import BudgetCategoryEditDialog from './BudgetCategoryEditDialog'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'

import eventBus from '@/eventBus'


export default {
  components: {
    PSearchInput,
    PBtnIcon,
    PDeleteDialog,
    BudgetCategoryEditDialog
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin
  ],


  data () {
    return {
      search: '',
      editDialog: false,
      editBudgetCategory: null,
      page: 1,
      pageCount: 0,
      itemsPerPage: 15,
      deleteDialog: false,
      deletedBudgetCategory: null,
      deletedBudgetCategoryData: null
    }
  },


  computed: {
    ...mapGetters('budgetCategory', ['budgetCategories']),
    ...mapGetters('changes', ['isItemChanged']),

    budgetCategoriesComputed () {
      return this.budgetCategories.map(budgetCategory => {
        return {
          ...budgetCategory,
          changed: this.isItemChanged(budgetCategory.id)
        }
      })
    },

    tableHeaders () {
      return [
        { text: this.$t('budgetCategory.table.name'), value: 'name' },
        { text: '', value: 'changed' },
        { text: this.$t('budgetCategory.table.account'), value: 'account' },
        { text: this.$t('budgetCategory.table.budget'), value: 'budget', align: 'end' },
        { text: this.$t('budgetCategory.table.income'), value: 'income', align: 'center' },
        { text: '', value: 'actions', align: 'end', width: 75, sortable: false, filterable: false }
      ]
    },

    searchSuggestions () {
      const items = this.budgetCategories.map(x => x.name)
      return items
    }
  },


  methods: {
    ...mapActions('budgetCategory', ['loadBudgetCategories', 'loadRelations', 'deleteBudgetCategory']),

    async searchBudgetCategories (val) {
      this.search = val
    },

    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        this.searchBudgetCategories(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.loadBudgetCategories()
      })
    },

    openEditDialog (budgetCategory) {
      this.editBudgetCategory = budgetCategory
      this.editDialog = true
    },

    async openDeleteDialog (budgetCategory) {
      try {
        if (budgetCategory) {
          this.deletedBudgetCategory = budgetCategory
          this.deletedBudgetCategoryData = {
            type: this.$t('entity.budgetCategory'),
            title: budgetCategory.name
          }
        }
        this.deleteDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    onItemDeleted () {
      this.showSnackbarSuccess(this.$t('budgetCategory.categoryDeleted'))
    }
  },

  async created () {
    try {
      this.initEventBus()
      this.startPageLoading()
      await this.loadBudgetCategories()
      if (this.$router.currentRoute.params?.goto) {
        await this.searchBudgetCategories(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
